import styled from "styled-components";
// Path assumes this file lives at
// src/components/sections/WelcomeScreen/Assessment/LifestyleStep.styles.js
import { DIET_C } from "../../../common/colors";

// ═══════════════════════════════════════════════════════════════════════════════
// LIFESTYLE STEP
// Reuses the --c-*/--sp-*/--r-sm/--t-fast custom properties that
// AssessmentStepShell.styles.js's <Section> scopes over the whole step —
// this file never redeclares them, same as GoalStep.styles.js.
// Card/toggle selected-state matches GoalCard: 2.4px DIET_C.primary
// border, no fill.
// ═══════════════════════════════════════════════════════════════════════════════

export const FieldGroup = styled.div`
  margin-bottom: var(--sp-6);
`;
FieldGroup.displayName = "FieldGroup";

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-4);
  margin-bottom: var(--sp-6);

  @media (max-width: 30em) {
    grid-template-columns: 1fr;
  }
`;
FieldRow.displayName = "FieldRow";

export const FieldLabel = styled.label`
  display: block;
  color: var(--c-text);
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: var(--sp-2);
`;
FieldLabel.displayName = "FieldLabel";

export const RequiredMark = styled.span`
  color: ${DIET_C.accent};
  margin-left: 2px;
`;
RequiredMark.displayName = "RequiredMark";

export const HelperValue = styled.span`
  color: var(--c-text-muted);
  font-weight: 500;
`;
HelperValue.displayName = "HelperValue";

const controlBase = `
  width: 100%;
  height: 44px;
  padding: 0 var(--sp-3);
  border-radius: var(--r-sm);
  background: var(--c-surface);
  color: var(--c-text);
  font-size: 0.92rem;
  transition: border-color var(--t-fast);
`;

export const Select = styled.select`
  ${controlBase}
  border: 1px solid ${DIET_C.secondary}88;

  &:focus-visible {
    outline: none;
    border-color: ${DIET_C.primary};
  }
`;
Select.displayName = "Select";

export const TimeInput = styled.input.attrs({ type: "time" })`
  ${controlBase}
  border: 1px solid ${({ $hasError }) => ($hasError ? DIET_C.accent : `${DIET_C.secondary}88`)};

  &:focus-visible {
    outline: none;
    border-color: ${DIET_C.primary};
  }
`;
TimeInput.displayName = "TimeInput";

// ─── Work type cards — same visual language as GoalStep's GoalCard ────────
export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns || 3}, 1fr);
  gap: var(--sp-3);
`;
CardGrid.displayName = "CardGrid";

export const WorkTypeCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--sp-1);
  min-height: 76px;
  padding: var(--sp-3) var(--sp-2);
  border-radius: var(--r-sm);
  background: var(--c-surface);
  color: var(--c-text);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: 2.4px solid ${({ $active }) => ($active ? DIET_C.primary : `${DIET_C.secondary}66`)};
  transition: border-color var(--t-fast);

  i {
    font-size: 1.15rem;
  }

  &:hover {
    border-color: ${DIET_C.primary};
  }

  &:focus-visible {
    outline: 3px solid var(--c-accent);
    outline-offset: 2px;
  }
`;
WorkTypeCard.displayName = "WorkTypeCard";

// ─── Water intake slider ───────────────────────────────────────────────────
export const RangeInput = styled.input.attrs({ type: "range" })`
  width: 100%;
  accent-color: ${DIET_C.primary};
`;
RangeInput.displayName = "RangeInput";

export const RangeScaleRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: var(--c-text-muted);
  margin-top: var(--sp-1);
`;
RangeScaleRow.displayName = "RangeScaleRow";

// ─── Stress emoji scale ─────────────────────────────────────────────────────
export const EmojiScaleRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 var(--sp-1);
`;
EmojiScaleRow.displayName = "EmojiScaleRow";

export const EmojiOption = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--sp-1);
  font-size: ${({ $active }) => ($active ? "1.7rem" : "1.4rem")};
  opacity: ${({ $active }) => ($active ? 1 : 0.4)};
  border-bottom: 2.4px solid ${({ $active }) => ($active ? DIET_C.primary : "transparent")};
  transition: all var(--t-fast);
`;
EmojiOption.displayName = "EmojiOption";

// ─── Smoking / Alcohol toggles ─────────────────────────────────────────────
export const ToggleRow = styled.div`
  display: flex;
  gap: var(--sp-2);
`;
ToggleRow.displayName = "ToggleRow";

export const ToggleButton = styled.button`
  flex: 1;
  padding: var(--sp-3) var(--sp-2);
  border-radius: var(--r-sm);
  background: var(--c-surface);
  color: var(--c-text);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: 2.4px solid ${({ $active }) => ($active ? DIET_C.primary : `${DIET_C.secondary}66`)};
  transition: border-color var(--t-fast);

  &:hover {
    border-color: ${DIET_C.primary};
  }
`;
ToggleButton.displayName = "ToggleButton";

// ─── Wellness disclaimer (smoking/alcohol = yes) ───────────────────────────
export const Disclaimer = styled.p`
  margin: 0;
  background: ${DIET_C.accent}14;
  border: 1px solid ${DIET_C.accent}55;
  border-radius: var(--r-sm);
  padding: var(--sp-3) var(--sp-4);
  color: var(--c-text);
  font-size: 0.85rem;
  line-height: 1.5;
`;
Disclaimer.displayName = "Disclaimer";
